<script lang="ts">
/**
 * The statuses this icon can render. The first four are the sales-document
 * lifecycle; `done` is the generic "processed, nothing left to do" state used by
 * records that are worked off rather than accepted or declined — the CRM
 * Kundenakte's offen/erledigt is the first of them (I-622).
 *
 * `done` renders IDENTICALLY to `accepted` (green, plain checkmark), deliberately.
 * The two do mean different things — agreed to by someone vs. finished by us — but
 * the value sets are disjoint (a document is never `done`, a file never `accepted`),
 * so they never appear side by side, and no reader would decode a second green
 * checkmark variant as that distinction anyway. `done` exists as a VALUE because the
 * data says `done` and grids render the raw cell value; keeping its look separate
 * would be a difference without information. Split them when something actually
 * shows both at once.
 */
export const STATUS_VALUES = [
  'draft',
  'open',
  'accepted',
  'declined',
  'done',
] as const satisfies readonly string[];

/**
 * Exported as a value, not only as a union, because consumers outside the kit
 * have to check a status at *runtime* — the portable form adapter validates
 * whatever a canvas definition declares. The one that kept a private copy of
 * this list stopped at four values and silently dropped `done`.
 */
export type StatusValue = (typeof STATUS_VALUES)[number];
import { CheckmarkIcon, DismissIcon, MoreHorizontalIcon, QuestionIcon } from '@blpde/nx-icons';
import { computed, HTMLAttributes } from 'vue';
</script>

<script setup lang="ts">
const { status, size = '1lh' } = defineProps<
  { status: StatusValue; size?: string } & /* @vue-ignore */ HTMLAttributes
>();

const iconSize = computed(() => `calc(${size} - 2 * var(--nxt-space-2xs))`);
</script>

<template>
  <div :class="['icon', status]" :style="`--size: ${size}`">
    <MoreHorizontalIcon v-if="status === 'draft'" filled :size="iconSize" />
    <QuestionIcon v-if="status === 'open'" filled :size="iconSize" />
    <CheckmarkIcon v-if="status === 'accepted' || status === 'done'" filled :size="iconSize" />
    <DismissIcon v-if="status === 'declined'" filled :size="iconSize" />
  </div>
</template>

<style scoped>
/* NxStatusIcon consumes the --nxt-status-icon-bg/-fg component tokens
   (I-596); each status re-points them at the matching semantic role. A soft
   tile with the glyph in the role's full colour and a thin ring of it — the
   same language as the kit's step discs and badges — so a column of statuses
   reads calmly instead of as a stack of solid squares. Draft is a dashed,
   neutral tile: nothing decided yet. */
.icon {
  --status-ring: color-mix(in srgb, var(--nxt-status-icon-fg) 35%, transparent);

  box-sizing: border-box;
  height: var(--size);
  width: var(--size);
  border-radius: calc(var(--size) * 0.3);
  position: relative;
  flex: none;
  background-color: var(--nxt-status-icon-bg);
  color: var(--nxt-status-icon-fg);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--status-ring);

  & > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: var(--size);
    width: var(--size);
  }

  &.draft {
    --nxt-status-icon-bg: var(--nxt-neutral-subtle-bg);
    --nxt-status-icon-fg: var(--nxt-fg-muted);

    box-shadow: none;
    border: var(--nxt-hairline) dashed var(--nxt-border-strong);
  }
  &.open {
    --nxt-status-icon-bg: var(--nxt-warning-subtle-bg);
    --nxt-status-icon-fg: var(--nxt-warning-bg);
  }
  /* One rule for both: see the StatusValue doc block — same look on purpose. */
  &.accepted,
  &.done {
    --nxt-status-icon-bg: var(--nxt-success-subtle-bg);
    --nxt-status-icon-fg: var(--nxt-success-bg);
  }
  &.declined {
    --nxt-status-icon-bg: var(--nxt-danger-subtle-bg);
    --nxt-status-icon-fg: var(--nxt-danger-bg);
  }
}

@media (forced-colors: active) {
  .icon {
    border: var(--nxt-hairline) solid CanvasText;
  }
}
</style>
